import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { BadgeIndianRupee, Plus, Trash2, Loader2 } from 'lucide-react';

/**
 * AdminPlansCard — admin-editable pricing/plan copy shown on the
 * TrialEnded and upgrade screens. Stored in Mongo `admin_config._id="plans"`
 * so we can adjust wording + prices without a code change. Reads via
 * `POST /admin/plans` (PIN-gated), writes via `POST /admin/plans/save`.
 * Public consumers read `GET /config/plans`.
 */
export default function AdminPlansCard({ debugPin }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedAt, setSavedAt] = useState('');

  const load = async () => {
    if (!debugPin) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/admin/plans', { pin: debugPin });
      setPlans(data?.plans || []);
      setSavedAt(data?.updated_at || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load plans.');
    } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addRow = () => setPlans((p) => [...p, { name: '', price: '', description: '' }]);
  const removeRow = (i) => setPlans((p) => p.filter((_, j) => j !== i));
  const patch = (i, field, val) => setPlans((p) => p.map((r, j) => j === i ? { ...r, [field]: val } : r));

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/admin/plans/save', { pin: debugPin, plans });
      setPlans(data.plans || []);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-plans-card" className="mt-3 bg-white border border-slate-200 shadow-sm text-slate-800 rounded-2xl p-4 text-[13px]">
      <div className="flex items-center justify-between mb-3">
        <div className="text-slate-900 font-bold uppercase tracking-wider text-[13px] flex items-center gap-2">
          <BadgeIndianRupee size={16}/> Plans & pricing
        </div>
        <div className="flex gap-1.5">
          <button data-testid="admin-plans-add" onClick={addRow}
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 hover:bg-slate-300 inline-flex items-center gap-1">
            <Plus size={12}/> Add plan
          </button>
          <button data-testid="admin-plans-save" onClick={save} disabled={busy}
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white disabled:opacity-60 inline-flex items-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin"/> : null}
            Save
          </button>
        </div>
      </div>

      {err && <div className="text-[13px] text-rose-600 mb-2">{err}</div>}
      {!!savedAt && <div className="text-[12px] text-slate-500 mb-2">Last saved: {new Date(savedAt).toLocaleString()}</div>}

      <div className="space-y-2">
        {plans.map((p, i) => (
          <div key={i} data-testid={`admin-plan-row-${i}`}
            className="border border-slate-300 rounded-xl p-3 flex flex-col gap-2 bg-white shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Plan name</label>
                <input data-testid={`admin-plan-name-${i}`} placeholder="e.g. Family monthly"
                  value={p.name} onChange={(e) => patch(i, 'name', e.target.value)}
                  className="w-full bg-slate-50 text-slate-900 text-[14px] font-bold rounded-lg px-3 py-2 outline-none border border-slate-300 focus:border-slate-500"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-amber-700 font-bold block mb-1">Price</label>
                <input data-testid={`admin-plan-price-${i}`} placeholder="₹499 / month"
                  value={p.price} onChange={(e) => patch(i, 'price', e.target.value)}
                  className="w-44 bg-amber-50 text-amber-900 text-[14px] font-bold rounded-lg px-3 py-2 outline-none border-2 border-amber-300 focus:border-amber-500"/>
              </div>
              <button data-testid={`admin-plan-remove-${i}`} onClick={() => removeRow(i)}
                title="Remove this plan"
                className="text-rose-600 hover:bg-rose-50 rounded-lg p-2 h-10 self-end"><Trash2 size={14}/></button>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Description</label>
              <textarea data-testid={`admin-plan-desc-${i}`} placeholder="What's included in this plan"
                value={p.description} onChange={(e) => patch(i, 'description', e.target.value)} rows={2}
                className="w-full bg-slate-50 text-slate-700 text-[13px] rounded-lg px-3 py-2 outline-none border border-slate-300 focus:border-slate-500 resize-none"/>
            </div>
          </div>
        ))}
        {!plans.length && (
          <div className="text-[13px] text-slate-500 italic py-3 text-center">
            No plans configured yet. The upgrade screen will show a generic "contact us" prompt.
          </div>
        )}
      </div>
    </div>
  );
}
